import React from "react";
import { Move, Plus, X } from "react-feather";
import { styled } from "styled-components";
import Button from "./Button";
import Label from "./Label";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import Description from "./Description";

interface Props<T> {
  component: React.FunctionComponent<{
    value: T;
    onChange: (value: T) => void;
  }>;
  extraProps?: any;
  value: T[];
  onChange: (value: T[]) => void;
  label?: React.ReactNode;
  newValue?: () => T;
  requireOne?: boolean;
  buttons?: React.ReactNode;
  labels?: React.ReactNode;
  fixedOrder?: boolean;
  required?: boolean;
  hasValue?: boolean;
  description?: string;
}

const ListElement = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;

  > div {
    flex-grow: 1;
  }
`;

const StyledButton = styled(Button)`
  margin-left: 5px;
`;

const Wrapper = styled.div`
  margin-bottom: 20px;
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: 5px;
  }
`;

const IconWrapper = styled.div`
  color: var(--gray050);
  flex-grow: 0 !important;
  margin-left: 10px;
  display: flex;
`;

const Header = styled.div`
  margin-bottom: 3px;
`;

const ListInput = <T,>(props: Props<T>) => {
  const {
    component,
    value: values,
    onChange,
    label,
    labels,
    newValue,
    requireOne,
    extraProps,
    buttons,
    fixedOrder,
    required,
    hasValue,
    description,
  } = props;

  const onSort = (from: number, to: number) => {
    const vals = [...values];
    vals.splice(to, 0, vals.splice(from, 1)[0]);
    onChange(vals);
  };

  const canDelete = !requireOne || values.length > 1;
  const canSort = !fixedOrder;

  let margin = 0;
  if (canDelete) {
    margin += 36;
  }
  if (canSort) {
    margin += 29;
  }

  return (
    <DragDropContext
      onDragEnd={(result) => {
        if (result.destination) {
          onSort(result.source.index, result.destination.index);
        }
      }}
    >
      <Wrapper>
        {label && (
          <Label label={label} required={required} hasValue={hasValue} />
        )}
        <Header style={{ marginRight: margin }}>{labels}</Header>
        <Droppable droppableId="listInput">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {values.map((value, index) => (
                <Draggable
                  draggableId={`${index}`}
                  index={index}
                  key={index}
                  isDragDisabled={!canSort}
                >
                  {(provided, _snapshot) => (
                    <ListElement
                      key={index}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                    >
                      {React.createElement(component, {
                        ...(extraProps || {}),
                        value,
                        onChange: (newValue) => {
                          onChange(
                            values.map((v, i) => (i === index ? newValue : v))
                          );
                        },
                      })}
                      {canSort && (
                        <IconWrapper {...provided.dragHandleProps}>
                          <Move size={19} />
                        </IconWrapper>
                      )}
                      {canDelete && (
                        <StyledButton
                          icon={X}
                          type="danger"
                          borderless
                          iconSize={21}
                          onClick={() => {
                            onChange(values.filter((v, i) => i !== index));
                          }}
                        />
                      )}
                    </ListElement>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        <Buttons>
          {buttons ? (
            buttons
          ) : (
            <Button
              label={`Add`}
              icon={Plus}
              type="success"
              onClick={() => {
                if (newValue) {
                  onChange([...values, newValue()]);
                }
              }}
            />
          )}
        </Buttons>
        <Description label={description} />
      </Wrapper>
    </DragDropContext>
  );
};

export default ListInput;
